import { Component, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SurveyReportService } from '../../services/survey-report.service';
import {
  ACTIVE_REPORT_STATUSES,
  SurveyReport,
  SurveyReportTrackingEvent,
  SurveyReportTrackingItem,
  SurveyReportTrackingState,
} from '../../models/survey-report.model';

type PhaseKey = 'analyses' | 'revisions' | 'build' | 'ready';
type PhaseState = 'skipped' | 'pending' | 'active' | 'done' | 'failed' | 'cancelled';

interface PhaseItem extends SurveyReportTrackingItem {
  id: string;
}

interface Phase {
  key: PhaseKey;
  state: PhaseState;
  items: PhaseItem[];
  counts: Record<SurveyReportTrackingState, number>;
  done: number;
  total: number;
}

const ITEM_ORDER: SurveyReportTrackingState[] = ['processing', 'failed', 'pending', 'completed'];

@Component({
  selector: 'app-modal-survey-report-progress',
  templateUrl: './modal-survey-report-progress.component.html',
  styleUrls: ['./modal-survey-report-progress.component.scss'],
  standalone: false,
})
export class ModalSurveyReportProgressComponent implements OnDestroy {
  report: SurveyReport | null;
  phases: Phase[] = [];
  events: SurveyReportTrackingEvent[] = [];
  expanded = new Set<PhaseKey>();
  now = Date.now();
  confirmingCancel = false;
  cancelling = false;
  private pollTimer: any;
  private clockTimer: any;

  constructor(
    public dialogRef: MatDialogRef<ModalSurveyReportProgressComponent>,
    private surveyReportService: SurveyReportService,
    @Inject(MAT_DIALOG_DATA) public data: { surveyId: number | string; report: SurveyReport | null; confirmCancel?: boolean },
  ) {
    this.apply(data.report);
    this.confirmingCancel = !!data.confirmCancel && this.isActive;
    this.clockTimer = setInterval(() => (this.now = Date.now()), 1000);
    this.pollTimer = setInterval(() => this.refresh(), 3000);
  }

  ngOnDestroy(): void {
    clearInterval(this.pollTimer);
    clearInterval(this.clockTimer);
  }

  get isActive(): boolean {
    return !!this.report && ACTIVE_REPORT_STATUSES.includes(this.report.status);
  }

  get elapsed(): string {
    if (!this.report?.created_at) {
      return '';
    }
    const end = this.report.completed_at && !this.isActive ? new Date(this.report.completed_at).getTime() : this.now;
    const seconds = Math.max(0, Math.floor((end - new Date(this.report.created_at).getTime()) / 1000));
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const pad = (value: number) => String(value).padStart(2, '0');
    return hours > 0 ? `${hours}:${pad(minutes % 60)}:${pad(seconds % 60)}` : `${pad(minutes)}:${pad(seconds % 60)}`;
  }

  get overallPercent(): number {
    const counted = this.phases.filter((phase) => phase.state !== 'skipped' && phase.key !== 'ready');
    if (counted.length === 0) {
      return 0;
    }
    const sum = counted.reduce((total, phase) => total + (phase.total > 0 ? phase.done / phase.total : phase.state === 'done' ? 1 : 0), 0);
    return Math.round((sum / counted.length) * 100);
  }

  toggle(key: PhaseKey): void {
    if (this.expanded.has(key)) {
      this.expanded.delete(key);
    } else {
      this.expanded.add(key);
    }
  }

  phasePercent(phase: Phase): number {
    return phase.total > 0 ? Math.round((phase.done / phase.total) * 100) : phase.state === 'done' ? 100 : 0;
  }

  download(): void {
    if (this.report?.status === 'COMPLETED') {
      this.surveyReportService.download(this.data.surveyId, this.report);
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  askCancel(): void {
    this.confirmingCancel = true;
  }

  keepGenerating(): void {
    this.confirmingCancel = false;
  }

  cancelGeneration(): void {
    if (!this.report || this.cancelling) {
      return;
    }
    this.cancelling = true;
    this.surveyReportService.cancel(this.data.surveyId, this.report.id, (report) => {
      this.cancelling = false;
      this.confirmingCancel = false;
      this.apply(report);
    }, () => {
      this.cancelling = false;
    });
  }

  private refresh(): void {
    if (!this.isActive || !this.report) {
      return;
    }
    this.surveyReportService.show(this.data.surveyId, this.report.id, (report) => this.apply(report));
  }

  private apply(report: SurveyReport | null): void {
    this.report = report;
    const tracking = report?.tracking;
    const status = report?.status;
    const waiting = status === 'WAITING_ANALYSES';
    const building = status === 'PENDING' || status === 'PROCESSING';
    const cancelled = status === 'CANCELLED';
    const finished = status === 'COMPLETED' || status === 'FAILED' || cancelled;

    const group = (key: 'analyses' | 'revisions'): Phase => {
      const items = Object.entries(tracking?.[key] || {})
        .map(([id, item]) => ({ ...item, id }))
        .sort((a, b) => ITEM_ORDER.indexOf(a.state) - ITEM_ORDER.indexOf(b.state));
      const counts = { pending: 0, processing: 0, completed: 0, failed: 0 } as Record<SurveyReportTrackingState, number>;
      items.forEach((item) => counts[item.state]++);
      const done = counts.completed + counts.failed;
      let state: PhaseState;
      if (items.length === 0) {
        state = 'skipped';
      } else if (done === items.length) {
        state = counts.failed > 0 ? 'failed' : 'done';
      } else {
        state = cancelled ? 'cancelled' : waiting ? 'active' : finished || building ? 'failed' : 'pending';
      }
      return { key, state, items, counts, done, total: items.length };
    };

    const buildState: PhaseState =
      status === 'COMPLETED' ? 'done' : status === 'FAILED' ? 'failed' : cancelled ? 'cancelled' : status === 'PROCESSING' ? 'active' : status === 'PENDING' ? 'active' : 'pending';
    const empty = { pending: 0, processing: 0, completed: 0, failed: 0 };

    this.phases = [
      group('analyses'),
      group('revisions'),
      {
        key: 'build',
        state: buildState,
        items: [],
        counts: empty,
        done: status === 'PROCESSING' || status === 'COMPLETED' ? report?.progress_done || 0 : 0,
        total: status === 'PROCESSING' || status === 'COMPLETED' ? report?.progress_total || 0 : 0,
      },
      { key: 'ready', state: status === 'COMPLETED' ? 'done' : status === 'FAILED' ? 'failed' : cancelled ? 'cancelled' : 'pending', items: [], counts: empty, done: 0, total: 0 },
    ];
    this.events = [...(tracking?.events || [])].reverse();
  }
}
